import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@environments/environment';

/**
 * Date: 03-09-2026 Author: Divy
 *
 * Mirrors com.jmotto.multi.reservation.promotion.PromotionOfferDTO on the
 * server. Pricing source-of-truth in this DTO is YieldMax base — no Wiki
 * fees, no coupon, no tax. The Apply flow lets the standard booking-quote
 * pipeline layer fees / coupon / tax back on top.
 */
/**
 * Per-direction apply state for dual-direction offers (added 2026-05-17).
 *
 * One instance per direction. Null until that direction is applied.
 *   prevState : populated when PREV is applied
 *   nextState : populated when NEXT is applied
 *
 * For single-direction offers (legacy or new single-side), the top-level
 * chargeXxx / appliedDate / appliedByUserId fields on PromotionOffer are the
 * source of truth and prevState/nextState are null.
 */
export interface DirectionApplyState {
	appliedDate?:         string;   // ISO LocalDateTime
	appliedByUserId?:     number;
	chargeAttempted?:     boolean;
	chargeApproved?:      boolean;
	chargeAmount?:        number;
	chargeCardMask?:      string;
	chargeTransactionId?: string;
	chargeErrorMessage?:  string;
}

export interface PromotionOffer {
	type:                 string;          // always "PROMOTION_EXTENSION_OFFER"
	version:              number;
	reservationId:        number;
	shortTermRentalId:    number;

	/**
	 * Legacy single-direction field. Null for new dual-direction offers — use
	 * `directions` instead. Kept so legacy rows still render unchanged.
	 */
	direction?:           'NEXT' | 'PREV';

	/** The single extra night (legacy single-direction). Null for dual. */
	offerDate?:           string;          // ISO date (e.g. "2026-09-21")

	// ---- Dual-direction fields (added 2026-05-17) ----
	/**
	 * Directions originally offered to the guest:
	 *   ['NEXT']          - day after checkout only
	 *   ['PREV']          - day before checkin only
	 *   ['PREV', 'NEXT']  - both available; guest can pick one or both
	 */
	directions?:          ('PREV' | 'NEXT')[];
	prevDate?:            string;          // ISO date — null when PREV not offered
	nextDate?:            string;          // ISO date — null when NEXT not offered
	prevYieldMaxPct?:     number;          // original YieldMax % for PREV (effectivePct may be the lower of the two)
	nextYieldMaxPct?:     number;          // original YieldMax % for NEXT
	prevState?:           DirectionApplyState;   // populated once PREV is applied
	nextState?:           DirectionApplyState;   // populated once NEXT is applied

	originalNights:       number;
	extendedNights:       number;
	origBaseTotal:        number;
	extendedBaseTotal:    number;
	extraNightYieldBase:  number;          // = extendedBaseTotal - origBaseTotal
	discountPct:          number;          // raw season discount %
	effectivePct:         number;          // post floor-cap %; for dual = min(prevYieldMaxPct, nextYieldMaxPct)
	extraNightDiscounted: number;          // = extraNightYieldBase * (1 - effectivePct/100)
	ownerFloorAMT?:       number;
	seasonNumber?:        number;
	seasonType?:          string;
	referralSourceId?:    number;          // 11=Website, 1=VRBO, 7=Google — determines auto-charge eligibility
	smsMsgId?:            number;
	createDate?:          string;   // str_meta_data.createDate — when the offer was sent (ISO LocalDateTime)
	expiresAt?:           string;   // createDate + 72 h — pre-computed by backend (ISO LocalDateTime)
	acceptedDate?:        string;
	appliedDate?:         string;
	appliedByUserId?:     number;
	// PARTIALLY_APPLIED = dual-direction offer where one side has been applied
	// and the other is still pending (reservationist can return within 72h to
	// apply the remaining side). Added 2026-05-17.
	status:               'OFFERED' | 'ACCEPTED' | 'APPLIED' | 'DECLINED' | 'EXPIRED' | 'PARTIALLY_APPLIED';

	// ---- CC charge result for single-direction offers (populated after apply) ----
	// For dual-direction offers these are NULL; read prevState / nextState instead.
	chargeAttempted?:     boolean;
	chargeApproved?:      boolean;
	chargeAmount?:        number;
	chargeCardMask?:      string;
	chargeTransactionId?: string;
	chargeErrorMessage?:  string;
}

/**
 * Mirrors com.jmotto.multi.reservation.promotion.PromotionApplyPreviewAPI.
 * Returned by both preview-apply (read-only) and apply (mutating) endpoints
 * — the server uses one shape so the UI can refresh the card on either
 * call without a second round-trip.
 *
 * Two pricing blocks:
 *   Guest-facing  — built from live order details, shown in the modal invoice.
 *   Internal/Wiki — raw YieldMax amounts used for the Wiki list-price entry.
 */
export interface PromotionFeeLineItem {
	feeName:   string;
	amount:    number;
	quantity:  number;
}

export interface PromotionApplyPreview {
	// ---- Decision ----
	decision:    'OK' | 'BLOCKED' | 'CHARGE_FAILED';
	reason?:     string;
	offerStatus?: string;
	reservationId?: number;

	// ---- Date change ----
	currentCheckIn?:  string;
	currentCheckOut?: string;
	currentNights?:   number;
	newCheckIn?:      string;
	newCheckOut?:     string;
	newNights?:       number;

	// ---- Internal / Wiki (not shown to guest) ----
	extraNightYieldBase?:                  number;
	effectivePct?:                         number;
	extraNightDiscounted?:                 number;
	couponPolicy?:                         string;
	recommendedListPriceAdjustmentAmount?: number;

	// ---- Guest-facing invoice for the +1 night ----
	guestNightlyRate?:        number;   // per-night list rate from the live order
	couponCode?:              string;   // e.g. "MEMORIALDAY20"
	couponDiscountPerNight?:  number;   // per-night coupon share (positive; UI shows as −)
	guestNightlyAfterCoupon?: number;   // rate after coupon
	extensionDiscountPct?:    number;   // our offer % applied to after-coupon rate
	extensionDiscountAmt?:    number;   // dollar discount (positive; UI shows as −)
	guestNightlyFinal?:       number;   // after coupon + extension discount
	extraNightFees?:          PromotionFeeLineItem[];
	extraNightFeesTotal?:     number;
	extraNightSubtotal?:      number;   // guestNightlyFinal + fees
	taxRate?:                 number;   // combined tax % (e.g. 22.63)
	taxAmt?:                  number;
	extraNightGuestTotal?:    number;   // what the guest pays

	// ---- CC charge result (populated by apply / apply-force) ----
	chargeAttempted?:      boolean;   // true = charge was attempted
	chargeApproved?:       boolean;   // true = approved, false = declined, null = not attempted
	chargeAmount?:         number;    // exact amount submitted
	chargeCardMask?:       string;    // e.g. "XXXX1234"
	chargeTransactionId?:  string;    // Authorize.net transaction ID on approval
	chargeErrorMessage?:   string;    // decline reason or "No saved card on file" etc.

	// ---- Dual-direction (added 2026-05-17) ----
	directionsApplied?:    ('PREV' | 'NEXT')[];
	directionPreviews?:    DirectionPreview[];
	/** Direction-scoped failures: e.g. { "PREV": "Adjacent night is no longer free" } */
	directionErrors?:      { [direction: string]: string };
}

/**
 * One direction's preview/apply detail (added 2026-05-17). Used when a single
 * API call covers BOTH directions — top-level pricing fields then carry the
 * combined totals; each entry here carries the per-direction breakdown.
 */
export interface DirectionPreview {
	direction:              'PREV' | 'NEXT';
	offerDate?:             string;           // ISO date — the extra night
	newCheckIn?:            string;
	newCheckOut?:           string;
	newNights?:             number;
	extensionDiscountPct?:  number;
	extensionDiscountAmt?:  number;
	guestNightlyFinal?:     number;
	extraNightSubtotal?:    number;
	taxAmt?:                number;
	extraNightGuestTotal?:  number;
	chargeAttempted?:       boolean;
	chargeApproved?:        boolean;
	chargeAmount?:          number;
	chargeTransactionId?:   string;
	chargeErrorMessage?:    string;
}

@Injectable({ providedIn: 'root' })
export class PromotionOfferService {

	constructor(private http: HttpClient) {}

	/**
	 * Returns the promotion offer for a reservation, or null if none exists.
	 *
	 * Backend returns an ApiResponse envelope:
	 *   { statusCode, message, data: { promotionOffer: {...} } }
	 * for 200 OK, and the same envelope shape with statusCode=204 (and no
	 * data.promotionOffer) when no offer exists. We unwrap the envelope
	 * here so callers get a clean PromotionOffer | null.
	 *
	 * Errors swallowed → null so the order page never blocks rendering on a
	 * missing/failed lookup.
	 */
	getOffer(reservationId: number): Observable<PromotionOffer | null> {
		if (!reservationId) return of(null);
		const url = `${environment.apiUrl}/reservation/${reservationId}/promotion-offer`;
		return this.http.get<any>(url).pipe(
			map((resp: any) => {
				if (!resp || resp.statusCode === 204) return null;
				return (resp.data && resp.data.promotionOffer) ? resp.data.promotionOffer as PromotionOffer : null;
			}),
			catchError(() => of(null))
		);
	}

	/**
	 * Non-mutating preview of what Apply will change. Unwraps ApiResponse envelope.
	 *
	 * For dual-direction offers, pass `directions` to preview a specific subset
	 * (e.g. ['NEXT'] to preview just the day-after extension). Omit to preview
	 * all originally-offered, still-unapplied directions.
	 */
	previewApply(reservationId: number, directions?: ('PREV' | 'NEXT')[]): Observable<PromotionApplyPreview | null> {
		if (!reservationId) return of(null);
		let url = `${environment.apiUrl}/reservation/${reservationId}/promotion-offer/preview-apply`;
		if (directions && directions.length > 0) {
			url += `?directions=${directions.join(',')}`;
		}
		return this.http.get<any>(url).pipe(
			map((resp: any) => {
				if (!resp) return null;
				return (resp.data && resp.data.promotionApplyPreview) ? resp.data.promotionApplyPreview as PromotionApplyPreview : null;
			}),
			catchError(() => of(null))
		);
	}

	/**
	 * Mark APPLIED. Server returns refreshed preview with status flipped.
	 *
	 * For dual-direction offers, `directions` selects which side(s) to apply
	 * in this call. Empty / omitted = apply all originally-offered, still-
	 * unapplied directions. Status flips to APPLIED only when all originally-
	 * offered directions are consumed; otherwise to PARTIALLY_APPLIED.
	 */
	applyOffer(reservationId: number, directions?: ('PREV' | 'NEXT')[]): Observable<PromotionApplyPreview | null> {
		if (!reservationId) return of(null);
		const url = `${environment.apiUrl}/reservation/${reservationId}/promotion-offer/apply`;
		const body = (directions && directions.length > 0) ? { directions } : {};
		return this.http.post<any>(url, body).pipe(
			map((resp: any) => {
				if (!resp) return null;
				return (resp.data && resp.data.promotionApplyPreview) ? resp.data.promotionApplyPreview as PromotionApplyPreview : null;
			}),
			catchError(() => of(null))
		);
	}

	/** Force-apply: extend without charge (used after reservationist confirms manual collection). */
	applyOfferForce(reservationId: number, directions?: ('PREV' | 'NEXT')[]): Observable<PromotionApplyPreview | null> {
		if (!reservationId) return of(null);
		const url = `${environment.apiUrl}/reservation/${reservationId}/promotion-offer/apply-force`;
		const body = (directions && directions.length > 0) ? { directions } : {};
		return this.http.post<any>(url, body).pipe(
			map((resp: any) => {
				if (!resp) return null;
				return (resp.data && resp.data.promotionApplyPreview) ? resp.data.promotionApplyPreview as PromotionApplyPreview : null;
			}),
			catchError(() => of(null))
		);
	}

	/** Mark DECLINED. Returns refreshed offer DTO, or null if no offer to decline. */
	declineOffer(reservationId: number): Observable<PromotionOffer | null> {
		if (!reservationId) return of(null);
		const url = `${environment.apiUrl}/reservation/${reservationId}/promotion-offer/decline`;
		return this.http.post<any>(url, {}).pipe(
			map((resp: any) => {
				if (!resp || resp.statusCode === 204) return null;
				return (resp.data && resp.data.promotionOffer) ? resp.data.promotionOffer as PromotionOffer : null;
			}),
			catchError(() => of(null))
		);
	}
}
